import { useEffect, useRef, useState, useCallback } from 'react';
import {
  VStack,
  SimpleGrid,
  Box,
  IconButton,
  Center,
  useColorModeValue,
  Tooltip,
  useToast,
  Image,
  HStack,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Spinner,
} from '@chakra-ui/react';
import { renderTemplateThumbnailFromPdfUrl } from './getTemplateThumbnail';
import { AdvancedAccordion } from './shared';
import { AlgorithmSettingsProps } from 'src/components/auto-shapes/types';
import { AutoShapeAlgorithmType, SymbolTemplate2D } from 'src/gql/graphql';
import { useSheetViewerV2 } from 'src/components/viewers/SheetsViewerV2/hooks/useSheetViewerV2';
import { usePreventClose } from 'src/components/orderContractor/AutoshapePopover';
import { ApiCoordinatesConverter } from 'src/components/viewers/SheetsViewerV2/utils/apiCoordinatesConverter';
import { VIEWER_TOP_OFFSET } from 'src/components/viewers/SheetsViewerV2/SheetsViewerV2.config';
import { useS3Client } from 'src/hooks/s3';
import { getSheetUrl } from 'src/components/viewers/SheetsViewerV2/utils/s3';
import type { Sheet } from 'src/hooks/sheets';

function TemplateThumbnail({
  pdfUrl,
  pageNumber,
  rect,
  fallback,
}: {
  pdfUrl: string;
  pageNumber: number;
  rect: { x: number; y: number; width: number; height: number };
  fallback: React.ReactNode;
}) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dataUrl = await renderTemplateThumbnailFromPdfUrl(
          pdfUrl,
          pageNumber,
          rect,
          {
            longestSidePx: 128,
          }
        );
        if (!cancelled) setSrc(dataUrl);
      } catch {
        if (!cancelled) setSrc(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl, pageNumber, rect]);

  if (!src) return <>{fallback}</>;
  return (
    <Image
      src={src}
      alt="symbol template"
      width="100%"
      height="100%"
      objectFit="contain"
    />
  );
}

function useSheetPdfUrlCache(
  projectId: string,
  sheets: Sheet[] | undefined,
  templates: SymbolTemplate2D[] | undefined
) {
  const s3Client = useS3Client(projectId);
  const [cache, setCache] = useState<Record<string, string>>({});

  useEffect(() => {
    const uniqueSheetIds = Array.from(
      new Set((templates ?? []).map((t) => t.sheetId))
    );
    if (!s3Client || !sheets || uniqueSheetIds.length === 0) return;
    let cancelled = false;
    (async () => {
      for (const sid of uniqueSheetIds) {
        if (cache[sid]) continue;
        try {
          const url = await getSheetUrl(s3Client, sheets, sid);
          if (!cancelled && url) {
            setCache((prev) => ({ ...prev, [sid]: url }));
          }
        } catch {
          // ignore
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [s3Client, sheets, templates, cache]);

  return cache;
}

function AddCard({ canPick, onAdd }: { canPick: boolean; onAdd: () => void }) {
  const addCardBorderColor = useColorModeValue('gray.300', 'gray.600');
  const addCardBg = useColorModeValue('gray.50', 'gray.700');
  const addCardHoverBg = useColorModeValue('gray.100', 'gray.600');
  const addCardIconColor = useColorModeValue('gray.500', 'gray.300');
  return (
    <Tooltip
      label={
        canPick
          ? 'Click to add a new symbol to search for'
          : 'Select a sheet to add symbols'
      }
      hasArrow
      openDelay={200}
    >
      <Box
        role="group"
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={addCardBorderColor}
        borderRadius="md"
        backgroundColor={addCardBg}
        _hover={{ backgroundColor: addCardHoverBg }}
        height="64px"
        cursor={canPick ? 'pointer' : 'not-allowed'}
        onClick={() => canPick && onAdd()}
      >
        <Center
          width="100%"
          height="100%"
          color={addCardIconColor}
          fontSize="3xl"
        >
          +
        </Center>
      </Box>
    </Tooltip>
  );
}

function CancelCard({ onCancel }: { onCancel: () => void }) {
  const cancelBorderColor = useColorModeValue('red.300', 'red.500');
  const cancelBg = useColorModeValue('red.50', 'red.600');
  const cancelIconColor = useColorModeValue('red.600', 'red.100');
  return (
    <Tooltip label="Click to cancel" hasArrow openDelay={200}>
      <Box
        role="group"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={cancelBorderColor}
        borderRadius="md"
        backgroundColor={cancelBg}
        height="64px"
        cursor="pointer"
        onClick={onCancel}
      >
        <Center
          width="100%"
          height="100%"
          color={cancelIconColor}
          fontSize="md"
          fontWeight="semibold"
        >
          Cancel
        </Center>
      </Box>
    </Tooltip>
  );
}

function AddOrCancelCard(props: {
  canPick: boolean;
  isPicking: boolean;
  onAdd: () => void;
  onCancel: () => void;
}) {
  const { canPick, isPicking, onAdd, onCancel } = props;
  return isPicking ? (
    <CancelCard onCancel={onCancel} />
  ) : (
    <AddCard canPick={canPick} onAdd={onAdd} />
  );
}

function TemplateCard({
  t,
  pdfUrl,
  onRemove,
}: {
  t: SymbolTemplate2D;
  pdfUrl?: string;
  onRemove: () => void;
}) {
  const templateBorderColor = useColorModeValue('gray.200', 'gray.600');
  const templateBg = useColorModeValue('gray.100', 'gray.700');
  const templateTextColor = useColorModeValue('gray.600', 'gray.300');

  return (
    <Box
      role="group"
      position="relative"
      borderWidth="1px"
      borderColor={templateBorderColor}
      borderRadius="md"
      height="64px"
      backgroundColor={templateBg}
    >
      {pdfUrl ? (
        <TemplateThumbnail
          pdfUrl={pdfUrl}
          pageNumber={t.pageNumber}
          rect={t.rect}
          fallback={
            <Center width="100%" height="100%">
              <Spinner
                size="sm"
                color={templateTextColor}
                thickness="2px"
                speed="0.5s"
              />
            </Center>
          }
        />
      ) : (
        <Center width="100%" height="100%">
          <Spinner
            size="sm"
            color={templateTextColor}
            thickness="2px"
            speed="0.5s"
          />
        </Center>
      )}
      <Tooltip label="Remove symbol" hasArrow openDelay={200}>
        <IconButton
          aria-label="Remove template"
          icon={
            <Box as="span" fontSize="sm">
              ×
            </Box>
          }
          variant="solid-no-transparent"
          backgroundColor="gray.900"
          color="gray.50"
          _hover={{ backgroundColor: 'gray.800' }}
          size="xs"
          position="absolute"
          top="-6px"
          right="-6px"
          zIndex={2}
          opacity={0}
          _groupHover={{ opacity: 1 }}
          onClick={onRemove}
        />
      </Tooltip>
    </Box>
  );
}

export function SymbolDetection2DSettings({
  value,
  onChange,
  onValidityChange,
}: AlgorithmSettingsProps<AutoShapeAlgorithmType.SymbolDetection_2D>) {
  const toast = useToast();
  const toastIdRef = useRef<string | number | undefined>(undefined);

  const [isPicking, setIsPicking] = useState(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const listenersJustAddedRef = useRef(false);
  const { viewer, activeSheet, projectId, sheets } = useSheetViewerV2();
  const setPreventClose = usePreventClose();

  const sheetUrlCache = useSheetPdfUrlCache(projectId, sheets, value.templates);

  // Sync preventClose with isPicking state to prevent popover from closing while picking
  // When isPicking becomes false, delay resetting preventClose to allow in-flight click events
  // from completing the pick to be processed with preventClose still true
  useEffect(() => {
    if (!setPreventClose) return;

    if (isPicking) {
      setPreventClose(true);
    } else {
      // Delay resetting preventClose to allow in-flight click events from completing the pick
      const timeoutId = setTimeout(() => {
        setPreventClose(false);
      }, 150);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPicking]); // setPreventClose is stable from useCallback

  useEffect(() => {
    const isValid = Boolean(value.sheetId) && Number.isFinite(value.pageNumber);
    onValidityChange(isValid);
  }, [value.sheetId, value.pageNumber, onValidityChange]);

  // Allow picking when a sheet is loaded in the viewer
  const canPick = Boolean(activeSheet?.data?.id);

  // External control for picking via global events
  useEffect(() => {
    const startHandler = () => {
      setIsPicking((prev) => (prev ? prev : true));
    };
    const cancelHandler = () => {
      setIsPicking(false);
    };
    window.addEventListener('symbol-detection:start', startHandler);
    window.addEventListener('symbol-detection:cancel', cancelHandler);
    return () => {
      window.removeEventListener('symbol-detection:start', startHandler);
      window.removeEventListener('symbol-detection:cancel', cancelHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // setPreventClose is stable

  useEffect(() => {
    if (isPicking) {
      if (!toast.isActive('symbol-detection-pick')) {
        const id = toast({
          id: 'symbol-detection-pick',
          title: 'Draw a rectangle',
          description: 'Click and drag on the sheet to select the symbol area.',
          status: 'info',
          isClosable: false,
          duration: null,
          position: 'bottom',
        });
        toastIdRef.current = id;
      }
    } else {
      toast.close('symbol-detection-pick');
      toastIdRef.current = undefined;
    }
  }, [isPicking, toast]);

  const beginPicking = useCallback(() => {
    if (!canPick) return;
    // Reset startRef to ensure we don't process stale mouse events
    startRef.current = null;
    setIsPicking(true);
  }, [canPick]);

  const cancelPicking = useCallback(() => {
    setIsPicking(false);
    if (toastIdRef.current) {
      toast.close(toastIdRef.current);
      toastIdRef.current = undefined;
    }
  }, [toast]);

  // Rectangle picking lifecycle
  useEffect(() => {
    if (!isPicking) return;

    const v = viewer.aggregated?.viewer as
      | Autodesk.Viewing.GuiViewer3D
      | undefined;
    const viewport = activeSheet?.data?.viewport;
    if (!v || !viewport) {
      return;
    }

    const container = v.container as HTMLDivElement | null;
    if (!container) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.style.position = 'absolute';
    overlay.style.zIndex = '1000';
    overlay.style.pointerEvents = 'none';
    overlay.style.border = '1px dashed rgba(66,153,225,0.9)';
    overlay.style.background = 'rgba(66,153,225,0.1)';
    overlayRef.current = overlay;
    container.appendChild(overlay);

    const stop = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const shouldIgnoreMouseEvent = (target: HTMLElement): boolean => {
      // Ignore clicks on buttons, inside interactive elements, or inside the edit autoshape popover
      const isInPopover = target.closest(
        '[data-edit-autoshape-popover="true"]'
      );
      const isButton =
        target.tagName === 'BUTTON' ||
        target.closest('button') ||
        target.closest('[role="button"]');
      const isInteractive =
        target.closest('a') ||
        target.closest('input') ||
        target.closest('select') ||
        target.closest('textarea');

      return Boolean(isButton || isInteractive || isInPopover);
    };

    const onMouseDown = (e: MouseEvent) => {
      // Ignore mousedown events immediately after listeners are added (from button click)
      if (listenersJustAddedRef.current) {
        return;
      }

      if (e.button !== 0) {
        return;
      }

      const target = e.target as HTMLElement;
      if (shouldIgnoreMouseEvent(target)) {
        return;
      }

      startRef.current = { x: e.clientX, y: e.clientY };
      stop(e);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!startRef.current) return;
      const rect = container.getBoundingClientRect();
      const x1 = startRef.current.x - rect.left;
      const y1 = startRef.current.y - rect.top;
      const x2 = e.clientX - rect.left;
      const y2 = e.clientY - rect.top;
      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const width = Math.abs(x2 - x1);
      const height = Math.abs(y2 - y1);
      overlay.style.left = `${left}px`;
      overlay.style.top = `${top}px`;
      overlay.style.width = `${width}px`;
      overlay.style.height = `${height}px`;
      stop(e);
    };

    const toWorld = (clientX: number, clientY: number) => {
      return v.impl.intersectGround(
        clientX,
        clientY - VIEWER_TOP_OFFSET
      ) as THREE.Vector3;
    };

    const onMouseUp = (e: MouseEvent) => {
      // Ignore the first mouseup after listeners are added (likely from the button click)
      if (listenersJustAddedRef.current) {
        return;
      }

      if (!startRef.current) {
        return;
      }
      const s = startRef.current;
      startRef.current = null;

      const p1 = toWorld(s.x, s.y);
      const p2 = toWorld(e.clientX, e.clientY);
      const converter = new ApiCoordinatesConverter(
        v as Autodesk.Viewing.GuiViewer3D,
        viewport
      );
      const a = converter.convertToApiPoint(p1);
      const b = converter.convertToApiPoint(p2);
      const minX = Math.min(a.x, b.x);
      const minY = Math.min(a.y, b.y);
      const maxX = Math.max(a.x, b.x);
      const maxY = Math.max(a.y, b.y);
      const newRect = {
        x: minX,
        y: minY,
        width: Math.max(0, maxX - minX),
        height: Math.max(0, maxY - minY),
      };

      const currentTemplates = (
        (value.templates ?? []) as SymbolTemplate2D[]
      ).slice();

      // Use the sheet/page from the currently loaded sheet in the viewer
      const drawnOnSheetId = activeSheet?.data?.id || value.sheetId || '';
      const drawnOnPage = activeSheet?.data?.page || value.pageNumber || 1;

      onChange({
        ...value,
        templates: [
          ...currentTemplates,
          {
            sheetId: drawnOnSheetId,
            pageNumber: drawnOnPage,
            rect: newRect,
          },
        ],
      });

      setIsPicking(false);
      if (overlayRef.current && overlayRef.current.parentNode) {
        overlayRef.current.parentNode.removeChild(overlayRef.current);
        overlayRef.current = null;
      }
      container.removeEventListener('mousedown', onMouseDown, true);
      container.removeEventListener('mousemove', onMouseMove, true);
      container.removeEventListener('mouseup', onMouseUp, true);
      stop(e);
    };

    // Track when listeners are first added to ignore the initial mouseup from the button click
    listenersJustAddedRef.current = true;
    const ignoreFirstMouseUpTimeout = setTimeout(() => {
      listenersJustAddedRef.current = false;
    }, 200);

    // Delay adding event listeners to avoid catching the click event that triggered beginPicking
    const timeoutId = setTimeout(() => {
      container.addEventListener('mousedown', onMouseDown, true);
      container.addEventListener('mousemove', onMouseMove, true);
      container.addEventListener('mouseup', onMouseUp, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(ignoreFirstMouseUpTimeout);
      if (overlayRef.current && overlayRef.current.parentNode) {
        overlayRef.current.parentNode.removeChild(overlayRef.current);
        overlayRef.current = null;
      }
      container.removeEventListener('mousedown', onMouseDown, true);
      container.removeEventListener('mousemove', onMouseMove, true);
      container.removeEventListener('mouseup', onMouseUp, true);
      startRef.current = null;
    };
  }, [
    isPicking,
    viewer.aggregated?.viewer,
    activeSheet?.data?.viewport,
    onChange,
    value,
    activeSheet?.data?.id,
    activeSheet?.data?.page,
  ]);

  const removeTemplateAt = useCallback(
    (idx: number) => {
      const currentTemplates = (value.templates ?? []) as SymbolTemplate2D[];
      onChange({
        ...value,
        templates: currentTemplates.filter((_x, i) => i !== idx),
      });
    },
    [onChange, value]
  );

  return (
    <VStack spacing={2} align="stretch" width="100%">
      <Text color="gray.500" fontSize="xs">
        Symbols
      </Text>
      <SimpleGrid columns={4} spacing={3}>
        <AddOrCancelCard
          canPick={canPick}
          isPicking={isPicking}
          onAdd={beginPicking}
          onCancel={cancelPicking}
        />

        {(value.templates ?? []).map((t, idx) => (
          <TemplateCard
            key={`${t.sheetId}-${t.pageNumber}-${idx}`}
            t={t}
            pdfUrl={sheetUrlCache[t.sheetId]}
            onRemove={() => removeTemplateAt(idx)}
          />
        ))}
      </SimpleGrid>

      <AdvancedAccordion>
        <VStack align="stretch" spacing={2}>
          <Text color="gray.500" fontSize="xs">
            Sensitivity
          </Text>
          <HStack spacing={3} align="center">
            <Slider
              colorScheme="purple"
              value={value.sensitivity ?? 0.75}
              min={0}
              max={1}
              step={0.01}
              onChange={(s) => onChange({ ...value, sensitivity: s })}
            >
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb boxSize={4} />
            </Slider>
            <Text width="48px" textAlign="right" fontSize="xs" color="gray.600">
              {Math.round((value.sensitivity ?? 0.75) * 100)}%
            </Text>
          </HStack>
        </VStack>
      </AdvancedAccordion>
    </VStack>
  );
}
